import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { ListTodo, X, Plus, Ban, Play } from "lucide-react";
import { TasksApi, BridgeApi, type TaskRow, type TaskStatus } from "../lib/api";
import { Pill, EmptyState, CenteredSpinner, Button } from "../components/primitives";
import { PageHeader } from "../components/PageScaffold";
import { useToast } from "../lib/toast";
import { relativeTime } from "../lib/format";
import { cn } from "../lib/cn";

// Kanban columns group the FSM states into the four lanes an operator scans:
// what's queued, what's being worked, what's stuck, what's finished.
const COLUMNS: { key: string; label: string; statuses: TaskStatus[] }[] = [
  { key: "backlog", label: "Backlog", statuses: ["backlog"] },
  { key: "progress", label: "In progress", statuses: ["active", "held"] },
  { key: "blocked", label: "Blocked", statuses: ["blocked"] },
  { key: "done", label: "Done", statuses: ["done", "cancelled"] },
];

function statusTone(s: TaskStatus): "neutral" | "accent" | "success" | "warning" | "danger" | "info" {
  switch (s) {
    case "active": return "accent";
    case "held": return "warning";
    case "blocked": return "danger";
    case "done": return "success";
    case "cancelled": return "neutral";
    default: return "info"; // backlog
  }
}

export default function Tasks() {
  const qc = useQueryClient();
  const [agent, setAgent] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["tasks", { agent }],
    queryFn: () => TasksApi.list({ agent: agent || undefined }),
    refetchInterval: 8000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  const rows = data?.tasks ?? [];
  const agents = [...new Set(rows.map((t) => t.owner_agent))].sort();
  const open = rows.filter((t) => !["done", "cancelled"].includes(t.status)).length;
  const blocked = rows.filter((t) => t.status === "blocked").length;

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Tasks"
        icon={ListTodo}
        subtitle={rows.length ? `${open} open · ${blocked} blocked · ${rows.length} total` : "durable per-agent work items — one focused task at a time"}
      />
      <div className="px-5 pb-2 flex items-center gap-2">
        <select value={agent} onChange={(e) => { setAgent(e.target.value); setSelectedId(null); }} className="h-7 text-xs w-40">
          <option value="">All agents</option>
          {agents.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Button size="sm" variant="ghost" onClick={() => setCreating((v) => !v)}><Plus className="size-3.5" />New task</Button>
        <span className="ml-auto text-[11px] text-fg-subtle font-mono">{rows.length}</span>
      </div>
      {creating && <CreateTask onDone={() => { setCreating(false); refresh(); }} defaultAgent={agent} />}

      <div className="flex-1 min-h-0 flex gap-3 px-5 pb-5 overflow-hidden">
        {isLoading ? (
          <CenteredSpinner />
        ) : error ? (
          <div className="p-5"><Pill tone="danger">Failed to load: {String(error)}</Pill></div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<ListTodo className="size-8" />} title="No tasks yet" description="Agents create tasks via my_task_create; they'll show here as they work them." />
        ) : (
          <>
            <div className="flex-1 min-w-0 flex gap-3 overflow-x-auto">
              {COLUMNS.map((col) => {
                const items = rows.filter((t) => col.statuses.includes(t.status));
                return (
                  <div key={col.key} className="flex-1 min-w-[220px] flex flex-col min-h-0 panel">
                    <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                      <span className="text-xs font-semibold text-fg">{col.label}</span>
                      <span className="text-[11px] text-fg-subtle font-mono">{items.length}</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {items.map((t) => (
                        <TaskCard key={t.id} t={t} selected={t.id === selectedId} onClick={() => setSelectedId(t.id)} />
                      ))}
                      {items.length === 0 && <div className="text-[11px] text-fg-subtle px-1 py-2">—</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedId && (
              <div className="w-[380px] flex-shrink-0 panel overflow-hidden flex flex-col">
                <TaskDetail id={selectedId} onClose={() => setSelectedId(null)} onChanged={refresh} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CreateTask({ onDone, defaultAgent }: { onDone: () => void; defaultAgent: string }) {
  const toast = useToast();
  const { data: personas } = useQuery({ queryKey: ["bridge", "personas"], queryFn: () => BridgeApi.personas() });
  const names = personas ? Object.keys(personas).sort() : [];
  const [agent, setAgent] = useState(defaultAgent);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const create = useMutation({
    mutationFn: () => TasksApi.create({ agent, title: title.trim(), description: description.trim() || undefined }),
    onSuccess: () => { toast.success("Task created (backlog)"); onDone(); },
    onError: (e) => toast.error("Create failed", String(e)),
  });
  const canSubmit = !!agent && title.trim().length > 0;
  return (
    <div className="mx-5 mb-2 panel p-3 space-y-2">
      <div className="flex items-center gap-2">
        <select value={agent} onChange={(e) => setAgent(e.target.value)} className="h-7 text-xs w-40">
          <option value="">Choose agent…</option>
          {names.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <input className="flex-1 text-sm" placeholder="Task title" value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <textarea className="w-full text-sm" rows={2} placeholder="Description / acceptance criteria (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-fg-subtle">Lands in the agent's backlog — it decides when to focus it.</span>
        <Button size="sm" className="ml-auto" disabled={!canSubmit} loading={create.isPending} onClick={() => create.mutate()}>Create</Button>
      </div>
    </div>
  );
}

function TaskCard({ t, selected, onClick }: { t: TaskRow; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left rounded-md border px-2.5 py-2 transition-colors",
        selected ? "border-accent bg-accent-subtle/30" : "border-border bg-surface-1 hover:bg-surface-2",
      )}
    >
      <div className="flex items-start gap-1.5">
        {t.focus === 1 && <span className="size-1.5 rounded-full bg-accent animate-pulse-dot mt-1.5 flex-shrink-0" title="focused" />}
        <span className="text-sm text-fg leading-snug line-clamp-2">{t.title}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        <Pill tone="info">{t.owner_agent}</Pill>
        {t.priority !== 0 && <span className="text-[10px] font-mono text-fg-subtle">P{t.priority}</span>}
        {t.note_count > 0 && <span className="text-[10px] font-mono text-fg-subtle">{t.note_count} note{t.note_count === 1 ? "" : "s"}</span>}
        <span className="ml-auto text-[10px] text-fg-subtle">{relativeTime(t.updated_at).replace(" ago", "")}</span>
      </div>
    </button>
  );
}

function TaskDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const toast = useToast();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tasks", id],
    queryFn: () => TasksApi.detail(id),
    refetchInterval: 8000,
  });
  const after = () => { refetch(); onChanged(); };
  const cancel = useMutation({
    mutationFn: () => TasksApi.cancel(id, "cancelled by operator"),
    onSuccess: () => { toast.success("Task cancelled"); after(); },
    onError: (e) => toast.error("Cancel failed", String(e)),
  });
  const reprioritise = useMutation({
    mutationFn: (p: number) => TasksApi.setPriority(id, p),
    onSuccess: () => after(),
    onError: (e) => toast.error("Reprioritise failed", String(e)),
  });
  const resume = useMutation({
    mutationFn: () => TasksApi.resume(id),
    onSuccess: (r) => { toast.success(`Resumed — ${r.owner} woken`); after(); },
    onError: (e) => toast.error("Resume failed", String(e)),
  });
  if (isLoading) return <CenteredSpinner />;
  if (error) return <div className="p-4"><Pill tone="danger">Failed: {String(error)}</Pill></div>;
  if (!data) return null;
  const { task: t, journal } = data;
  const terminal = t.status === "done" || t.status === "cancelled";
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-3 pb-3 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Pill tone="info">{t.owner_agent}</Pill>
            <Pill tone={statusTone(t.status)}>{t.status}</Pill>
            {t.focus === 1 && <Pill tone="accent">focused</Pill>}
          </div>
          <button onClick={onClose} className="text-fg-subtle hover:text-fg"><X className="size-4" /></button>
        </div>
        <h2 className="text-sm font-semibold text-fg mt-2 leading-snug">{t.title}</h2>
        <div className="text-[11px] text-fg-subtle mt-1.5 flex items-center gap-2 flex-wrap font-mono">
          {t.origin && <span>origin {t.origin}</span>}
          <span>created {relativeTime(t.created_at)}</span>
          {t.done_at && <span>closed {relativeTime(t.done_at)}</span>}
        </div>
        {!terminal && (
          <div className="flex items-center gap-2 mt-2.5">
            <span className="text-[11px] text-fg-subtle">priority</span>
            <button className="size-5 rounded border border-border text-fg-muted hover:bg-surface-2 leading-none" disabled={reprioritise.isPending} onClick={() => reprioritise.mutate(t.priority - 1)}>−</button>
            <span className="text-xs font-mono text-fg w-5 text-center">{t.priority}</span>
            <button className="size-5 rounded border border-border text-fg-muted hover:bg-surface-2 leading-none" disabled={reprioritise.isPending} onClick={() => reprioritise.mutate(t.priority + 1)}>+</button>
            {t.focus !== 1 && (
              <Button size="sm" variant="ghost" className="ml-auto" loading={resume.isPending}
                title={`Focus this task and wake ${t.owner_agent} with its context`}
                onClick={() => resume.mutate()}>
                <Play className="size-3.5" />Resume
              </Button>
            )}
            <Button size="sm" variant="danger" className={t.focus === 1 ? "ml-auto" : ""} loading={cancel.isPending}
              onClick={() => { if (confirm(`Cancel "${t.title}" (owned by ${t.owner_agent})?`)) cancel.mutate(); }}>
              <Ban className="size-3.5" />Cancel
            </Button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {t.description && <p className="text-xs text-fg-muted whitespace-pre-wrap leading-relaxed">{t.description}</p>}
        {t.blocked_reason && <div className="panel border-danger/40 px-2.5 py-1.5 text-xs text-danger">blocked: {t.blocked_reason}</div>}
        {t.resolution && <div className="panel border-success/40 px-2.5 py-1.5 text-xs text-fg-muted">resolution: {t.resolution}</div>}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">Checkpoints · {journal.length}</div>
          {journal.length === 0 ? (
            <div className="text-[11px] text-fg-subtle">no checkpoints yet</div>
          ) : (
            <ol className="space-y-2">
              {journal.map((c, i) => (
                <li key={i} className="text-xs border-l-2 border-border pl-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] text-fg-subtle font-mono">
                    <span className={cn(c.kind === "note" ? "text-accent" : c.kind === "created" ? "text-success" : "text-warning")}>{c.kind}</span>
                    <span>{c.by}</span>
                    <span className="ml-auto">{relativeTime(c.ts)}</span>
                  </div>
                  <div className="text-fg-muted mt-0.5 whitespace-pre-wrap break-words">{c.body}</div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
